import { z } from "zod/v4"
import { LiveIdSchema, SubscriptionIdSchema, UserId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { PowerDenormalized } from "./power"
import { Scope } from "../lib/payment"
import { SubscriptionKindNormal, SubscriptionKindPremium, SubscriptionKindPremiumPlus } from "./subscription/subscription-kind"
import { SubscriptionPlatform } from "./subscription/subscription"
import { assertNever } from "../lib/assert-never"
import { UserLivePointRateOverride, type UserLivePointRateEntry } from "./conversion-rate"

export const UserPrivateCoinBalance = z.object({
  scope: z.nativeEnum(Scope),
  total: z.number(),
  expiry: z.number().nullable(),
})

export type UserPrivateCoinBalance = z.infer<typeof UserPrivateCoinBalance>

const CoinTransactionCommon = z.object({
  nonce: z.string().min(1),
  requested_at: CreatedAt,
})

export const CoinTransaction = z.discriminatedUnion("kind", [
  CoinTransactionCommon.extend({
    kind: z.literal("power"),
    value: z.object({
      live_id: LiveIdSchema,
      power: PowerDenormalized,
      created_at: CreatedAt,
    })
  }),
  CoinTransactionCommon.extend({
    kind: z.literal("shop"),
  }),
  CoinTransactionCommon.extend({
    kind: z.literal("subscription"),
  }),
  CoinTransactionCommon.extend({
    kind: z.literal("stamp-card"),
  }),
  CoinTransactionCommon.extend({
    kind: z.literal("super-lane-unlock"),
  }),
])

export type CoinTransaction = z.infer<typeof CoinTransaction>

export const UserSubscription = z.union([
  z.object({
    kind: SubscriptionKindNormal,
    previous: z.object({
      ended_reason: z.union([
        z.literal("expire"),
        z.literal("cancel"),
        z.literal("revoke"),
      ]).describe("終了理由。expire: 期限切れ、cancel: ユーザーによる解約、revoke: 管理者による取り消し"),
      ended_at: z.number().describe("サブスクリプションが終了した時刻（Unix timestamp）"),
      kind: z.union([SubscriptionKindPremium, SubscriptionKindPremiumPlus]).describe("以前のプラン種別"),
      expire_at: z.number().nullable().describe("以前のサブスクリプションの有効期限（Unix timestamp）"),
      platform: SubscriptionPlatform.optional().describe("以前のサブスクリプションのプラットフォーム。iap: App Store, iab: Google Play, admin: 管理者付与"),
    }).nullable().describe("以前の有料サブスクリプション情報。nullの場合は以前の契約情報なし。"),
  }),
  z.object({
    kind: z.union([SubscriptionKindPremium, SubscriptionKindPremiumPlus]),
    status: z.union([
      z.literal("active"),
      z.literal("cancelled"),
      z.literal("payment_failed"),
      z.literal("revoked"),
    ]).describe("サブスクリプションの状態。active: 有効、cancelled: 解約済み（有効期限まで利用可能）、payment_failed: 自動更新失敗（猶予期間中）、revoked: 管理者による取り消し"),
    expire_at: z.number().nullable().describe("サブスクリプションの有効期限（Unix timestamp）。nullの場合は無期限。"),
    platform: SubscriptionPlatform.describe("サブスクリプションのプラットフォーム。iap: App Store, iab: Google Play, admin: 管理者付与"),
    subscription_id: SubscriptionIdSchema.optional().describe("サブスクリプション契約のID。"),
  }).describe("有料プラン"),
])

export type UserSubscription = z.infer<typeof UserSubscription>

/**
 * ユーザーの国判定情報。IPアドレスから判定した国コードを保持する。
 * BGMの国別再生制限のために使用される。
 */
export const UserGeo = z.object({
  country_code: z.string().regex(/^[A-Z]{2}$/).nullable().describe("ISO 3166-1 alpha-2形式の国コード。判定不能時はnull。"),
  ip: z.string().describe("判定に使用したIPアドレス"),
  updated_at: z.number().describe("国判定を更新した時刻（Unix timestamp ms）"),
  ip_version: z.enum(["v4", "v6"]).describe("判定に使用したIPアドレスのバージョン"),
  data_version: z.string().describe("判定に使用したIPデータのバージョン（例: 2026-01-05）"),
})

export type UserGeo = z.infer<typeof UserGeo>

/**
 * ユーザーの生年月情報。コイン購入時の年齢確認に使用される。
 * 一度登録すると変更できない。
 */
export const UserBirthday = z.object({
  year: z.number().int().min(1900).max(3000).describe("生年（西暦）"),
  month: z.number().int().min(1).max(12).describe("生月（1-12）"),
})

export type UserBirthday = z.infer<typeof UserBirthday>

/**
 * ユーザーの規約同意情報（単一の規約タイプ）。
 * 最新の同意のみを保持し、履歴はBigQueryエクスポートで管理される。
 */
export const UserTermsAgreementValue = z.object({
  version: z.string().describe("同意した規約バージョン（セマンティックバージョニング形式）"),
  agreed_at: z.number().describe("同意日時（Unix timestamp ms）"),
})

export type UserTermsAgreementValue = z.infer<typeof UserTermsAgreementValue>

/**
 * ユーザーの規約同意情報（複数の規約タイプ対応）。
 * 各規約タイプをキーとしたマップ構造。すべてのキーが存在するとは限らない。
 */
export const UserTermsAgreements = z.object({
  "terms-of-service": UserTermsAgreementValue.optional(),
  "privacy": UserTermsAgreementValue.optional(),
})

export type UserTermsAgreements = z.infer<typeof UserTermsAgreements>

/**
 * LIVEポイント交換同意の種類。
 */
export const LivePointRedemptionConsentKind = z.enum(["dotmoney"])
export type LivePointRedemptionConsentKind = z.infer<typeof LivePointRedemptionConsentKind>

/**
 * LIVEポイント交換同意リクエスト（クライアントAPI用）
 */
export const AgreeToLivePointRedemptionConsentRequest = z.object({
  kind: LivePointRedemptionConsentKind,
})
export type AgreeToLivePointRedemptionConsentRequest = z.infer<typeof AgreeToLivePointRedemptionConsentRequest>

/**
 * LIVEポイント交換における同意情報（単一の同意種別）。
 * 交換ごとに毎回同意を取得し、最新の同意日時のみを保持する。
 * 履歴はBigQueryエクスポート（user_privates_raw_changelog）で管理される。
 */
export const LivePointRedemptionConsentValue = z.object({
  accepted_at: z.number().describe("同意日時（Unix timestamp ms）"),
})

export type LivePointRedemptionConsentValue = z.infer<typeof LivePointRedemptionConsentValue>

/**
 * LIVEポイント交換における同意情報（複数の交換先対応）。
 * 各交換先をキーとしたマップ構造。すべてのキーが存在するとは限らない。
 */
export const LivePointRedemptionConsents = z.object({
  dotmoney: LivePointRedemptionConsentValue.optional().describe("ドットマネー交換の同意情報。個人／国内居住／規約違反なしの3項目に対する一括同意。"),
})

export type LivePointRedemptionConsents = z.infer<typeof LivePointRedemptionConsents>

/**
 * ユーザーのチュートリアル進行状況。
 * チュートリアルのバージョン、現在のステップ、完了状態を記録する。
 */
export const UserTutorial = z.object({
  version: z.number().int().min(1).describe("チュートリアルのバージョン"),
  step: z.number().int().min(0).describe("現在のチュートリアルステップ"),
  completed: z.boolean().describe("チュートリアルが完了したかどうか"),
  updated_at: z.number().describe("進行状況を更新した時刻（Unix timestamp ms）"),
})

export type UserTutorial = z.infer<typeof UserTutorial>

/**
 * チュートリアル進行状況更新リクエスト（クライアントAPI用）
 * updated_atはサーバー側で自動設定されるため、リクエストには含まない。
 */
export const UpdateTutorialProgressRequest = z.object({
  version: z.number().int().min(1).describe("チュートリアルのバージョン"),
  step: z.number().int().min(0).describe("現在のチュートリアルステップ"),
  completed: z.boolean().describe("チュートリアルが完了したかどうか"),
})

export type UpdateTutorialProgressRequest = z.infer<typeof UpdateTutorialProgressRequest>

export const UserPrivateLivePoint = z.object({
  total_balance: z.number().int().describe("LIVEポイント残高合計。grant で加算、exchange / expire で減算される。"),
  exchange_blocked: z.boolean().optional().describe("true の場合、LIVEポイントの交換（ドットマネー・無償コイン）が停止されている。管理者操作で設定。"),
})

export type UserPrivateLivePoint = z.infer<typeof UserPrivateLivePoint>

export const UserPrivateDocumentMeta= z.object({
  coin_transaction: CoinTransaction.nullable(),
  coin_balance_list: z.array(UserPrivateCoinBalance).default([]),
  // TODO: 全ユーザーにsubscriptionが追加されたらoptionalを外す
  subscription: UserSubscription.optional().describe("現在のサブスクリプション情報。第三者に非公開。"),
  geo: UserGeo.optional().describe("ユーザーの国判定情報。BGMの国別再生制限のために使用。"),
  birthday: UserBirthday.nullable().optional().describe("ユーザーの生年月情報。コイン購入時の年齢確認に使用。一度登録すると変更不可。"),
  terms_agreements: UserTermsAgreements.optional().describe("規約の同意情報。各規約タイプをキーとしたマップ構造。"),
  tutorial: UserTutorial.optional().describe("チュートリアルの進行状況。"),
  first_account_linked_at: z.number().optional().describe("初めてアカウント連携（Google, Apple, 電話番号等）でサインインした日時（Unix timestamp ms）。匿名でない状態に初めてなった時点を記録。"),
  live_point_redemption_consents: LivePointRedemptionConsents.optional().describe("LIVEポイント交換の同意情報。交換先ごとの同意日時を保持する。"),
  live_point_rate: UserLivePointRateOverride.optional().describe("ユーザー個別の変換レート。セクション構造（from_power 等）で標準レートと同じ形式。"),
  live_point: UserPrivateLivePoint.optional().describe("LIVEポイントの残高サマリー。付与時に FieldValue.increment で更新される。"),
})

export type UserPrivateDocumentMeta = z.infer<typeof UserPrivateDocumentMeta>

export const UserPrivateDocumentBody = z.intersection(
  UserPrivateDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type UserPrivateDocumentBody = z.infer<typeof UserPrivateDocumentBody>

export type UserPrivateDocument = UserPrivateDocumentBody & {
  id: UserId,
}

export class UserPrivateDocuments {

  static toDocumentData(doc: UserPrivateDocumentBody): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): UserPrivateDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: UserId(doc.id),
      coin_transaction: data["coin_transaction"],
      coin_balance_list: data["coin_balance_list"] ?? [],
      subscription: data["subscription"],
      geo: data["geo"],
      birthday: data["birthday"],
      terms_agreements: data["terms_agreements"],
      tutorial: data["tutorial"],
      first_account_linked_at: data["first_account_linked_at"],
      live_point_redemption_consents: data["live_point_redemption_consents"],
      live_point_rate: data["live_point_rate"],
      live_point: data["live_point"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }

  /**
   * ユーザー個別レートが有効かどうかを判定する。
   * effective_from <= now の場合のみ有効として返す。
   */
  static getActiveFromPowerRate(
    livePointRate: UserLivePointRateOverride | null | undefined,
    now: number,
  ): UserLivePointRateEntry | null {
    const rate = livePointRate?.from_power
    if (!rate) {
      return null
    }
    if (now < rate.effective_from) {
      return null
    }
    return rate
  }
}

export class UserSubscriptions {
  /**
   * 指定した時点でユーザーがプレミアム会員であったかどうかを判定する
   *
   * 以下の条件を満たす場合にtrue:
   * - サブスクリプションが有料プラン（premium/premium-plus）
   * - サブスクリプションの状態が active または cancelled（解約済みでも有効期限内は利用可能）
   * - 有効期限が設定されている場合、指定した時点が有効期限内
   *
   * または、現在normalプランでも以前プレミアムだった場合:
   * - 以前のサブスクリプションの有効期限が指定した時点より後であれば、その時点ではプレミアム会員だった
   *
   * @param subscription 現在のユーザーサブスクリプション
   * @param timestampToCheckPremiumStatusAt プレミアム会員であったかを判定する時点（Unix timestamp）
   * @returns 指定した時点でプレミアム会員であった場合は true
   */
  static isPremiumUser(
    subscription: UserSubscription | null,
    timestampToCheckPremiumStatusAt: number
  ): boolean {
    if (!subscription) {
      return false
    }

    if (subscription.kind === "premium" || subscription.kind === "premium-plus") {
      // subscriptionがactiveかcancelledである場合、少なくとも無効な契約ではなかったことがわかるので事前に判定する。
      if (subscription.status !== "active" && subscription.status !== "cancelled") {
        return false
      }

      return !(subscription.expire_at !== null && subscription.expire_at <= timestampToCheckPremiumStatusAt);
    } else if (subscription.kind === "normal") {
      // 現在normalでも、以前プレミアムだった場合は、その時点でプレミアムだったかを判定する
      if (subscription.previous !== null) {
        // revokeの場合は即座に利用不可となるため、ended_atを使用
        if (subscription.previous.ended_reason === "revoke") {
          return timestampToCheckPremiumStatusAt < subscription.previous.ended_at
        }
        // cancel/expireの場合は有効期限まで利用可能なため、expire_atを使用
        // 以前のサブスクリプションの有効期限が指定した時点より後であれば、その時点ではプレミアム会員だった
        if (subscription.previous.expire_at === null) {
          // 無期限だった場合は、その時点ではプレミアム会員だった
          return true
        }
        return timestampToCheckPremiumStatusAt < subscription.previous.expire_at
      }
      return false
    } else {
      assertNever(subscription.kind)
    }
  }

  /**
   * 別プラットフォームでアクティブなサブスクリプションがあるかどうかを判定する
   *
   * 以下の条件を満たす場合にtrue:
   * - 現在のサブスクリプションが有料プラン（premium/premium-plus）
   * - 現在のサブスクリプションのプラットフォームが新しいプラットフォームと異なる
   * - 現在のサブスクリプションの状態が active または payment_failed
   *
   * @param subscription 現在のユーザーサブスクリプション
   * @param newPlatform 新しいプラットフォーム
   * @returns 別プラットフォームでアクティブなサブスクがある場合は true
   */
  static hasActiveSubscriptionOnDifferentPlatform(
    subscription: UserSubscription | undefined,
    newPlatform: SubscriptionPlatform
  ): boolean {
    if (!subscription) {
      return false
    }

    if (subscription.kind === "normal") {
      return false
    }

    if (subscription.platform === newPlatform) {
      return false
    }

    return subscription.status === "active" || subscription.status === "payment_failed"
  }

  /**
   * 別プラットフォームでの再契約が可能かどうかを判定する
   *
   * 以下の条件を満たす場合に再契約可能:
   * - 現在のサブスクリプションが有料プラン（premium/premium-plus）
   * - 現在のサブスクリプションのプラットフォームが新しいプラットフォームと異なる
   * - 現在のサブスクリプションの状態が cancelled
   * - 現在のサブスクリプションの有効期限が設定されている
   * - 現在時刻が有効期限内
   *
   * @param subscription 現在のユーザーサブスクリプション
   * @param newPlatform 新しいプラットフォーム
   * @param now 現在時刻（Unix timestamp）
   * @returns 再契約可能な場合は true
   */
  static canRecontractOnDifferentPlatform(
    subscription: UserSubscription | undefined,
    newPlatform: SubscriptionPlatform,
    now: number
  ): boolean {
    if (!subscription) {
      return false
    }

    if (subscription.kind === "normal") {
      return false
    }

    if (subscription.platform === newPlatform) {
      return false
    }

    if (subscription.status !== "cancelled") {
      return false
    }

    if (subscription.expire_at === null) {
      return false
    }

    return now < subscription.expire_at
  }
}
